import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Minus, Plus, Trash2, Tag } from "lucide-react";
import { useAuth } from "@clerk/react";
import { http, img, inr, errMsg } from "@/lib/api";
import { useShop, useSeo } from "@/lib/store";

export function useQuote() {
  const { cart, coupon } = useShop();
  const { getToken, isSignedIn } = useAuth();
  const [q, setQ] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    if (!cart.length) { setQ(null); return; }
    const items = cart.map(({ product_id, size, color, qty }) => ({ product_id, size, color, qty }));
    (async () => {
      const token = isSignedIn ? await getToken() : null;
      const r = await http.post("/cart/quote", { items, coupon }, token ? { headers: { Authorization: `Bearer ${token}` } } : {});
      setQ(r.data); setErr("");
    })().catch((e) => setErr(errMsg(e)));
  }, [cart, coupon, isSignedIn, getToken]);
  return { q, err };
}

export function Summary({ q, children }) {
  if (!q) return null;
  return (
    <div className="bg-[#171717] p-6 space-y-3 font-mono text-sm" data-testid="order-summary">
      <div className="flex justify-between"><span className="text-grey">Subtotal</span><span data-testid="summary-subtotal">{inr(q.subtotal)}</span></div>
      {q.discounts.map((d) => <div key={d.label} className="flex justify-between text-neon"><span>{d.label}</span><span>-{inr(d.amount)}</span></div>)}
      <div className="flex justify-between"><span className="text-grey">Shipping</span><span data-testid="summary-shipping">{q.shipping ? inr(q.shipping) : "FREE"}</span></div>
      <div className="flex justify-between border-t border-white/10 pt-3 text-lg font-semibold"><span>Total</span><span data-testid="summary-total">{inr(q.total)}</span></div>
      {children}
    </div>
  );
}

export function CouponBox({ q }) {
  const { coupon, setCoupon } = useShop();
  const [code, setCode] = useState(coupon);
  return (
    <div>
      <form onSubmit={(e) => { e.preventDefault(); setCoupon(code.trim().toUpperCase()); }} className="flex gap-2">
        <div className="flex-1 relative"><Tag className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-grey" /><input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Coupon code (try DEN15)" className="field !pl-9 uppercase" data-testid="coupon-input" /></div>
        <button className="btn-ghost px-4" data-testid="coupon-apply-button">Apply</button>
      </form>
      {coupon && q?.coupon_message && <p className={`text-xs mt-2 font-mono ${q.coupon_message === "applied" ? "text-neon" : "text-red-400"}`} data-testid="coupon-message">
        {q.coupon_message === "applied" ? `${coupon} APPLIED` : q.coupon_message}{" "}
        <button className="underline text-grey" onClick={() => { setCoupon(""); setCode(""); }} data-testid="coupon-remove">remove</button></p>}
    </div>
  );
}

export default function Cart() {
  useSeo("Your Cart");
  const { cart, setQty, remove } = useShop();
  const { q, err } = useQuote();
  if (!cart.length) return (
    <div className="py-32 text-center" data-testid="cart-empty"><p className="font-display text-5xl">YOUR BAG IS EMPTY.</p><Link to="/shop" className="btn-neon mt-8">Shop the drop</Link></div>
  );
  return (
    <div className="max-w-[1300px] mx-auto px-4 sm:px-8 py-12" data-testid="cart-page">
      <h1 className="font-display text-5xl sm:text-6xl">YOUR BAG</h1>
      <div className="grid lg:grid-cols-[1.6fr_1fr] gap-10 mt-10">
        <div className="divide-y divide-white/10 border-y border-white/10">
          {cart.map((i) => (
            <div key={i.key} className="flex gap-4 py-5" data-testid={`cart-item-${i.slug}-${i.size}`}>
              <Link to={`/product/${i.slug}`} className="w-24 sm:w-28 aspect-[3/4] bg-[#171717] shrink-0"><img src={img(i.image)} alt={i.title} className="w-full h-full object-cover" /></Link>
              <div className="flex-1 min-w-0">
                <Link to={`/product/${i.slug}`} className="font-semibold hover:text-neon">{i.title}</Link>
                <p className="font-mono text-xs text-grey mt-1">SIZE {i.size}{i.color && ` · ${i.color.toUpperCase()}`}</p>
                <p className="font-mono mt-2">{inr(i.price)}</p>
                <div className="flex items-center gap-4 mt-3">
                  <div className="flex items-center border border-[#333]">
                    <button onClick={() => setQty(i.key, i.qty - 1)} className="p-2" data-testid="cart-qty-decrease"><Minus className="w-3 h-3" /></button>
                    <span className="w-8 text-center font-mono text-sm" data-testid="cart-qty-value">{i.qty}</span>
                    <button onClick={() => setQty(i.key, i.qty + 1)} className="p-2" data-testid="cart-qty-increase"><Plus className="w-3 h-3" /></button>
                  </div>
                  <button onClick={() => remove(i.key)} className="text-grey hover:text-red-400 flex items-center gap-1 text-xs" data-testid="cart-remove-button"><Trash2 className="w-4 h-4" />Remove</button>
                </div>
              </div>
              <p className="font-mono font-semibold">{inr(i.price * i.qty)}</p>
            </div>
          ))}
        </div>
        <div className="space-y-4 lg:sticky lg:top-24 self-start">
          <CouponBox q={q} />
          {err && <p className="text-red-400 text-sm" data-testid="cart-error">{err}</p>}
          <Summary q={q}>
            <Link to="/checkout" className="btn-neon w-full mt-4" data-testid="checkout-button">Checkout</Link>
            <p className="text-[11px] text-grey">Buy 2+ tees → 10% off auto-applied. Free shipping over ₹999.</p>
          </Summary>
        </div>
      </div>
    </div>
  );
}
